/**
 * FeatureTour
 *
 * Guided tour that highlights key app sections after onboarding.
 * Uses driver.js (vanilla JS, zero deps, React 19 compatible).
 *
 * Tour steps cover:
 *   1. Sidebar navigation overview
 *   2. Health Dashboard (RhythmCard)
 *   3. AI Agents section in sidebar
 *   4. Knowledge Base concept
 *   5. Your Profile
 *
 * Styled with MiEROS dark glassmorphism theme.
 * User can dismiss via "Don't show again" — persisted in Firebase RTDB.
 *
 * Requires: npm install driver.js
 */

import React, { useEffect, useRef } from 'react';
import { useOnboarding } from '../context/OnboardingContext';

// ---- Tour step definitions (driver.js format) ----
const TOUR_STEPS = [
  {
    element: '.sidebar',
    popover: {
      title: 'Navigation',
      description:
        'This is your sidebar navigation. Access all sections of MiExistence from here — health, agents, marketplace, and more.',
      side: 'right',
      align: 'start',
      showButtons: ['next', 'close'],
      showProgress: true,
    },
  },
  {
    element: '.body.flex-grow-1',
    popover: {
      title: 'Health Dashboard',
      description:
        'Your main dashboard shows health metrics, circadian rhythm data, and key insights at a glance.',
      side: 'bottom',
      align: 'start',
      showButtons: ['next', 'previous', 'close'],
      showProgress: true,
    },
  },
  {
    element: '[data-tour="ai-agents"]',
    popover: {
      title: 'AI Agents',
      description:
        "Create and manage AI agents that learn about you over time. Each agent has its own personality, knowledge base, and conversation history. Upload documents to your agent's knowledge base for more accurate, personalized responses.",
      side: 'right',
      align: 'start',
      showButtons: ['next', 'previous', 'close'],
      showProgress: true,
    },
  },
  {
    element: '.header',
    popover: {
      title: 'Your Profile',
      description:
        'Use the header to access your profile, notifications, and account settings. Your AI agent profile grows automatically as you use the app.',
      side: 'bottom',
      align: 'end',
      showButtons: ['next', 'previous', 'close'],
      doneBtnText: "Got it!",
      showProgress: true,
    },
  },
];

/**
 * FeatureTour component.
 * Uses driver.js — starts the tour after a short delay when not dismissed.
 */
const FeatureTour = () => {
  const { featureTourDismissed, setFeatureTourDismissed } = useOnboarding();
  const driverRef = useRef(null);

  useEffect(() => {
    if (featureTourDismissed) return;

    let driverObj = null;
    let timer = null;

    const runTour = async () => {
      try {
        const { driver } = await import('driver.js');
        await import('driver.js/dist/driver.css');

        driverObj = driver({
          showProgress: true,
          nextBtnText: 'Next',
          prevBtnText: 'Back',
          doneBtnText: "Got it!",
          progressText: '{{current}} of {{total}}',
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          overlayOpacity: 0.6,
          stagePadding: 10,
          stageRadius: 8,
          popoverClass: 'mieros-tour-popover',
          steps: TOUR_STEPS,
          onDestroyStarted: () => {
            // User closed or finished — persist dismissal
            setFeatureTourDismissed(true);
          },
        });

        driverRef.current = driverObj;
        driverObj.drive();
      } catch (err) {
        // driver.js not installed — tour silently does not run
        console.warn('FeatureTour: driver.js not available', err?.message);
      }
    };

    // Delay so layout has time to mount and targets exist
    timer = setTimeout(runTour, 1500);

    return () => {
      if (timer) clearTimeout(timer);
      if (driverRef.current?.isActive?.()) {
        driverRef.current.destroy();
      }
    };
  }, [featureTourDismissed, setFeatureTourDismissed]);

  // driver.js renders its own DOM — we render nothing
  return null;
};

export default FeatureTour;
